import { join } from 'node:path';
import React, { useMemo, useState } from 'react';
import type { FC } from 'react';
import { isType, match } from 'variant';
import kebabCase from 'lodash/kebabCase.js';
import { AuthProvider, SiteAuthProvider } from '@wix/cli-auth';
import { Box, Spinner, useAsync } from '@wix/cli-ui-kit';
import { getRepoType, createPackageManager } from '@wix/package-manager';
import type { CloudProvider } from '@wix/backend-as-a-service-client';
import { WelcomeMessage } from './WelcomeMessage.js';
import { ChooseFolder } from './ChooseFolder.js';
import { CreateProject } from './CreateProject.js';
import { GenerateProject } from './GenerateProject.js';
import { CreateBusiness } from './CreateBusiness.js';
import { ChooseBusinessName } from './ChooseBusinessName.js';
import { ChooseTemplate } from './ChooseTemplate.js';
import { ReleaseProject } from './ReleaseProject.js';
import { ChooseVibeSetup } from './ChooseVibeSetup.js';
import type { VibeSetupChoiceType } from './ChooseVibeSetup.js';
import { VibeRepositorySetupPolling } from './VibeRepositorySetupPolling.js';
import type {
  CommandOptions,
  TemplateSource,
} from '../parse-command-options.js';
import type { ProjectData } from '../hooks/create-project.js';

export const CreateHeadlessSiteCommand: FC<{
  targetParentFolder: string;
  commandOptions: CommandOptions;
  template?: TemplateSource;
  folderName?: string;
  businessName?: string;
  cloudProviders?: CloudProvider[];
  publish?: boolean;
}> = ({
  targetParentFolder,
  commandOptions,
  template: defaultTemplate,
  publish,
  folderName: defaultFolderName,
  businessName: defaultBusinessName,
  cloudProviders,
}) => {
  const [businessName, setBusinessName] = useState<string | undefined>(
    defaultBusinessName
  );
  const [selectedTemplate, setSelectedTemplate] = useState<
    TemplateSource | undefined
  >(defaultTemplate);
  const [folderName, setFolderName] = useState<string | undefined>(
    defaultFolderName
  );
  const [businessId, setBusinessId] = useState<string>();
  const [projectData, setProjectData] = useState<ProjectData>();
  const [generationDone, setGenerationDone] = useState<boolean>(false);
  const [vibeSetupChoice, setVibeSetupChoice] = useState<VibeSetupChoiceType>();
  const [vibeSetupDone, setVibeSetupDone] = useState<boolean>(false);

  const projectFolder = useMemo(
    () => (folderName ? join(targetParentFolder, folderName) : undefined),
    [targetParentFolder, folderName]
  );

  const { status } = useAsync(async () => {
    const repoType = await getRepoType(targetParentFolder);
    const packageManager = await createPackageManager(repoType);

    return { packageManager };
  }, []);

  const isVibeCompatible = useMemo(
    () =>
      isType(selectedTemplate, 'HeadlessApp') &&
      selectedTemplate.vibeCompatible,
    [selectedTemplate]
  );

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text="Loading..." />,
    Success: ({ result: { packageManager } }) => {
      return (
        <Box
          flexDirection="column"
          borderStyle="single"
          borderColor="gray"
          borderBottom={false}
          borderRight={false}
          borderTop={false}
          paddingLeft={1}
        >
          <WelcomeMessage />

          {!defaultBusinessName && (
            <ChooseBusinessName
              onSubmit={({ businessName }) => setBusinessName(businessName)}
            />
          )}

          {!defaultTemplate && businessName && (
            <ChooseTemplate
              onSubmit={({ template }) => {
                setSelectedTemplate(template);
              }}
            />
          )}

          {businessName && selectedTemplate && (
            <AuthProvider>
              <CreateBusiness
                businessName={businessName}
                businessTemplateId={selectedTemplate.siteTemplateId}
                onDone={({ businessId }) => setBusinessId(businessId)}
              />
            </AuthProvider>
          )}

          {!defaultFolderName &&
            businessName &&
            selectedTemplate &&
            businessId && (
              <ChooseFolder
                initialFolderName={folderName ?? kebabCase(businessName)}
                targetParentFolder={targetParentFolder}
                onSubmit={({ folderName }) => setFolderName(folderName)}
              />
            )}

          {businessName && selectedTemplate && businessId && folderName && (
            <SiteAuthProvider siteId={businessId}>
              <CreateProject
                businessId={businessId}
                businessName={businessName}
                cloudProviders={cloudProviders}
                onSubmit={(data) => setProjectData(data)}
              />
            </SiteAuthProvider>
          )}

          {businessName &&
            selectedTemplate &&
            businessId &&
            projectData &&
            folderName && (
              <GenerateProject
                folderName={folderName}
                businessId={businessId}
                projectData={projectData}
                packageManager={packageManager}
                targetParentFolder={targetParentFolder}
                commandOptions={commandOptions}
                selectedTemplate={selectedTemplate}
                onDone={(isDone) => setGenerationDone(isDone)}
              />
            )}

          {businessName &&
            selectedTemplate &&
            businessId &&
            projectData &&
            folderName &&
            generationDone &&
            !vibeSetupChoice &&
            isVibeCompatible && (
              <ChooseVibeSetup
                onSubmit={(choice) => setVibeSetupChoice(choice)}
              />
            )}

          {businessName &&
            selectedTemplate &&
            businessId &&
            projectData &&
            projectFolder &&
            generationDone &&
            isVibeCompatible &&
            isType(vibeSetupChoice, 'SetupVibe') && (
              <AuthProvider>
                <VibeRepositorySetupPolling
                  projectId={projectData.projectId}
                  projectFolder={projectFolder}
                  onSubmit={() => setVibeSetupDone(true)}
                />
              </AuthProvider>
            )}

          {businessName &&
            selectedTemplate &&
            businessId &&
            projectData &&
            folderName &&
            generationDone &&
            (vibeSetupDone ||
              isType(vibeSetupChoice, 'Skip') ||
              !isVibeCompatible) && (
              <ReleaseProject
                folderName={folderName}
                packageManager={packageManager}
                targetParentFolder={targetParentFolder}
                projectData={projectData}
                siteId={businessId}
                releaseNonInteractive={publish}
              />
            )}
        </Box>
      );
    },
  });
};
